import React from 'react';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { SectionHeader, TableBody, Tag, Text, TextButtonAccordion, Wrapper } from 'components';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { BusinessDayStartItem } from 'screens/SiteSettings/DaySegments/sections/BusinessDayStart';
import { GracePeriodItem } from 'screens/SiteSettings/DaySegments/sections/GracePeriod';
import { LoadingSkeleton } from 'screens/SiteSettings/styles';
import { CreditCardFeesItem } from 'screens/SiteSettings/TipCalculator/sections/CreditCardFees';
import { SalesGroupItem } from 'screens/SiteSettings/TipCalculator/sections/SalesGroups';
import { TipShareItem } from 'screens/SiteSettings/TipCalculator/sections/TipCalculations';
import { DEFAULT_POOL_NAME, IPaymentSet } from 'types';
import { centsToDollarsDisplay, formatTimeRange, sortAlphaNumerically, toTitleCase } from 'utils';
import PosIntegrationsSettings from './PosIntegrationsSettings';
import { IExportData } from './types';

const Layout = styled.section`
  flex-direction: column;
  width: 100%;
`;

const SettingSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  padding: ${toRem(theme.space.lg)};

  &:not(:last-child),
  > div:not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const DetailsContainer = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)};
`;

const DetailsListItem = styled.li`
  margin-left: ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.xxs)};
  color: ${theme.color.neutral[500]};

  span {
    color: ${theme.color.neutral[500]};
  }
`;

const DetailsRow = styled.div<{ isHeader?: boolean; tableView?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding-top: ${toRem(theme.space.sm)};
  width: 100%;

  & > *:first-child {
    word-break: break-word;
  }

  ${({ isHeader }) =>
    isHeader &&
    css`
      p {
        color: ${theme.color.neutral[500]};
      }
    `}

  ${({ tableView }) =>
    tableView &&
    css`
      &:not(:last-child) {
        padding-bottom: ${toRem(theme.space.sm)};
        border-bottom: 1px solid ${theme.color.neutral[200]};
      }
    `}
`;

const EmployeesTable = styled.table`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border-collapse: collapse;
  background-color: ${theme.color.neutral[50]};
  padding: ${toRem(theme.space.lg)};
`;

const TableHeader = styled.thead`
  display: flex;
  flex-flow: row nowrap;
  gap: ${toRem(theme.space.sm)};
`;

const TableRow = styled.tr`
  display: flex;
  flex-flow: row nowrap;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const SharedCellStyles = css`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  font-size: ${toRem(theme.font.size.xs)};
  flex: 1;

  &:first-child {
    flex: 2;
  }

  &:nth-child(2) {
    justify-content: center;
  }

  &:not(:first-child):not(:nth-child(2)) {
    justify-content: flex-end;
  }
`;

const TableHeaderCell = styled.th`
  ${SharedCellStyles}
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.medium};
`;

const TableCell = styled.td`
  ${SharedCellStyles}
  color: ${theme.color.neutral[900]};
  padding: ${toRem(theme.space.sm)} 0;
`;

const EmployeeWithholdingsTable = ({ paymentSet }: { paymentSet: IPaymentSet }) => {
  const employeeMap = new Map(paymentSet.payouts.map((payout) => [payout.employee.id, payout.employee.name]));
  const employeeWithholdings = paymentSet.withholdings.employees
    .reduce((acc, emp) => {
      const employeeName = employeeMap.get(emp.id);
      if (employeeName) acc.push({ ...emp, name: employeeName });
      return acc;
    }, [])
    .sort(sortAlphaNumerically('name'));
  return (
    <EmployeesTable data-cy="employee-withholdings-table">
      <TableHeader data-cy="employee-withholdings-table-header">
        <TableRow>
          <TableHeaderCell>Employee</TableHeaderCell>
          <TableHeaderCell>Type</TableHeaderCell>
          <TableHeaderCell>Minimum</TableHeaderCell>
          <TableHeaderCell>Rate</TableHeaderCell>
          <TableHeaderCell>Cap</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody data-cy="employee-withholdings-table-body">
        {employeeWithholdings.map((withholdings) => (
          <TableRow key={`${withholdings.id}-withholdings`}>
            <TableCell>
              <Text size="xsmall" fontWeight="medium" allowWordWrap>
                {withholdings.name}
              </Text>
            </TableCell>
            <TableCell>
              <Tag label={toTitleCase(withholdings.settings.type)} appearance={Appearance.NEUTRAL} size={Size.SMALL} />
            </TableCell>
            <TableCell>
              <Text size="xsmall">{centsToDollarsDisplay(withholdings.settings.minimum ?? 0)}</Text>
            </TableCell>
            <TableCell>
              <Text size="xsmall">{withholdings.settings.rate ?? 0}%</Text>
            </TableCell>
            <TableCell>
              <Text size="xsmall">{withholdings.settings.cap ? centsToDollarsDisplay(withholdings.settings.cap) : '--'}</Text>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </EmployeesTable>
  );
};

interface ISettingsReportProps {
  header: (data?: IExportData) => React.ReactNode;
  paymentSet: IPaymentSet;
  isLoading?: boolean;
}

const SettingsReport = ({ header, paymentSet, isLoading }: ISettingsReportProps) => {
  const { calculations, paymentDate, withholdings } = paymentSet || {};
  const sortedPointValueRules = calculations?.results[0].rules?.filter((rule) => !rule.default).sort(sortAlphaNumerically('contributor.name'));
  const sortedTipShareRules = sortedPointValueRules.filter((rule) => !!rule.sharePolicy);
  const defaultPoolRule = calculations?.results[0].rules?.find((rule) => rule.default);

  // Prefer the site's day segments as they were captured at payment time (KICK-5150). A segment that
  // ended with no activity never produces a result row, so deriving the list from `results` silently
  // drops it. Payments recorded before segments were captured have no stored list and keep the
  // original results-derived rendering — that fallback is the whole backward-compatibility story here.
  // Stored segments come from site config and never include the calculator's default pool, so only the
  // results branch filters it out.
  const storedDaySegments = calculations?.settings?.daySegments;
  const daySegments = storedDaySegments?.length
    ? storedDaySegments.map(({ name, start, end }, idx) => ({ key: `${name}_${idx}`, name, start, end }))
    : (calculations?.results.filter((result) => result.segment !== DEFAULT_POOL_NAME).map((result, idx) => ({ key: `${result.segmentId}_${idx}`, name: result.segment, start: result.start, end: result.end })) ?? []);

  return (
    <>
      {header()}
      <Layout>
        {isLoading || !paymentSet ? (
          <>
            {[1, 2, 3].map((_, idx) => (
              <SettingSection key={`loading-skeleton-${idx}`}>
                <LoadingSkeleton width="100%" height={toRem(40)} />
              </SettingSection>
            ))}
          </>
        ) : (
          <>
            {/* Day Segment Settings */}
            <SettingSection data-cy="day-segment-settings">
              <SectionHeader title="Day Segments">
                <Wrapper direction="row" alignItems="center" gap={theme.space.lg}>
                  {calculations?.settings.gracePeriodPolicy && (
                    <Wrapper direction="row" alignItems="center" gap={12}>
                      <Text size="xsmall">Grace Period</Text>
                      <GracePeriodItem gracePeriodPolicy={calculations.settings.gracePeriodPolicy} />
                    </Wrapper>
                  )}
                  {calculations?.settings.businessDayStart && (
                    <Wrapper direction="row" alignItems="center" gap={12}>
                      <Text size="xsmall">Start of Business Day</Text>
                      <BusinessDayStartItem businessDayStart={calculations.settings.businessDayStart} timezone={calculations.settings.timezone} />
                    </Wrapper>
                  )}
                </Wrapper>
              </SectionHeader>
              <TextButtonAccordion title={dayjs(paymentDate).format('MMMM Do, YYYY')} buttonText="Segment Details">
                <DetailsContainer>
                  <DetailsRow data-cy="day-segment-header" isHeader>
                    <Text size="xsmall" fontWeight="medium">
                      Day Segment
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      Hours
                    </Text>
                  </DetailsRow>
                  {daySegments.map((segment) => (
                    <DetailsRow key={segment.key} tableView>
                      <Text data-cy="segment-name" size="xsmall" fontWeight="medium">
                        {segment.name}
                      </Text>
                      <Text data-cy="segment-hours" size="xsmall">
                        {formatTimeRange(segment.start, segment.end)}
                      </Text>
                    </DetailsRow>
                  ))}
                </DetailsContainer>
              </TextButtonAccordion>
            </SettingSection>
            {/* Sales Group Settings */}
            {!!calculations?.results[0].meta?.categoryGroups.length && (
              <SettingSection data-cy="sales-group-settings">
                <SectionHeader title="Sales Groups" />
                {calculations?.results[0].meta?.categoryGroups?.sort(sortAlphaNumerically('name')).map((group, idx) => (
                  <SalesGroupItem key={`sales-group-${group.name}_${idx}`} categoryGroup={group} disabled />
                ))}
              </SettingSection>
            )}
            {/* Tip Share Settings */}
            <SettingSection data-cy="tip-share-settings">
              <SectionHeader title="Tip Shares">{typeof calculations?.settings.creditCardPercentFee === 'number' && <CreditCardFeesItem percentFee={calculations.settings.creditCardPercentFee} />}</SectionHeader>
              {sortedTipShareRules.map((rule, i) => (
                <TipShareItem key={`tip-share-${i}`} tipShare={rule} disabled />
              ))}
            </SettingSection>
            {/* Default Pool Settings */}
            {defaultPoolRule && (
              <SettingSection data-cy="default-pool-settings">
                <SectionHeader title="Default Pool" />
                <TipShareItem tipShare={defaultPoolRule} disabled />
              </SettingSection>
            )}
            {/* Point Values Settings */}
            <SettingSection data-cy="point-values-settings">
              <SectionHeader title="Point Values" />
              <TextButtonAccordion title="Jobs and Points" buttonText="Point Values">
                <DetailsContainer>
                  <DetailsRow data-cy="job-point-values-header">
                    <Text size="xsmall" fontWeight="medium">
                      Job Title
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      Points
                    </Text>
                  </DetailsRow>
                  {sortedPointValueRules.map((rule, idx) => (
                    <DetailsListItem key={`job-point-values_${idx}`}>
                      <DetailsRow>
                        <Text data-cy="job-title" as="span" size="xsmall" fontWeight="medium">
                          {toTitleCase(rule.contributor.name)}
                        </Text>
                        <Text data-cy="job-points" as="span" size="xsmall">
                          {rule.value}
                        </Text>
                      </DetailsRow>
                    </DetailsListItem>
                  ))}
                </DetailsContainer>
              </TextButtonAccordion>
            </SettingSection>
            {/* Withholdings Settings */}
            {withholdings?.employees.length && (
              <SettingSection data-cy="withholding-settings">
                <SectionHeader title="Withholdings" />
                <TextButtonAccordion title="Employees Withholding Settings" buttonText="Settings">
                  <EmployeeWithholdingsTable paymentSet={paymentSet} />
                </TextButtonAccordion>
              </SettingSection>
            )}
            {/* POS Integrations Settings */}
            {calculations?.settings && <PosIntegrationsSettings integrations={calculations.settings.integrations} />}
          </>
        )}
      </Layout>
    </>
  );
};

export default SettingsReport;
